import React, { useState, useEffect } from "react";
import { Row, Col, Form, Input, Button, Select, Tooltip } from "antd";
import {
  CopyOutlined,
  PlusOutlined,
  DownloadOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons";
import RequestHandler from "../../util/RequestHandler";
import { defaultValidateMessages } from "./Helper";
import config from "../../util/ApiforContext";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const TransactionBankInfoUpdateForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [activeBankId, setActiveBankId] = useState();
  const [activeVersion, setActiveVersion] = useState();
  const [bankRequirements, setBankRequirements] = useState({});
  const [callbackUrl, setCallbackUrl] = useState();
  const [useKeyBanks, setUseKeyBanks] = useState([134]);

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue(props.data);
      setActiveBankId(props.data.bankId);
      setActiveVersion(props.data.version);
    }
  }, [props.data]);

  useEffect(() => {
    if (activeBankId)
      getBankInfoRequirements(activeBankId, props.data?.version);
  }, [activeBankId]);

  useEffect(() => {
    if (activeVersion == 62001) {
      getGarantiCallbackUrl();
    } else {
      setCallbackUrl();
    }
  }, [activeVersion]);

  const getBankInfoRequirements = (bankId, version) => {
    RequestHandler.get(
      `/Bank/GetBankColumnsByBankId/${bankId}/${version}`
    ).then((response) => {
      setBankRequirements({
        isRequiredAccountInfo: response.isRequiredAccountInfo,
        ...response.bankInfoRequiredColumns,
        ...response.bankInfoAccountRequiredColumns,
        versions: response.versions,
      });
    });
  };

  const getGarantiCallbackUrl = () => {
    RequestHandler.get(`/TransactionBankInfo/GarantiApiCallbackUrl`).then(
      (response) => {
        setCallbackUrl(response.data);
      }
    );
  };

  const onActiveVersionChange = (version) => {
    setActiveVersion(version);
    getBankInfoRequirements(activeBankId, version);
  };

  const onFinish = (values) => {
    RequestHandler.put(`/TransactionBankInfo`, values)
      .then((response) => {
        setLoading(false);
        if (response.success) {
          form.resetFields();

          if (props.onSuccess) props.onSuccess();
        }
      })
      .catch(function (error) {
        setLoading(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
  };

  const downloadKeyFile = () => {
    window.open(
      `${
        config.apiUrl
      }/TenantKeyFile/DownloadPublicKey/AccountTransaction/${activeBankId}?t=${RequestHandler.getToken()}`
    );
  };

  const FormItemLabelWithTooltip = ({ label, tooltip }) => {
    return (
      <>
        {label}{" "}
        <Tooltip title={
          <span>
            {tooltip?.split('*').filter(part => part.trim() !== '').map((line, index) => (
              <div key={index}> * {line}</div>
            ))}
          </span>
        } overlayStyle={{ maxWidth: "450px" }}>
          <QuestionCircleOutlined
            className="gx-text-blue"
            style={{ marginLeft: 5,marginRight:3, fontSize: "120%" }}
          />
        </Tooltip>
      </>
    );
  };

  return (
    <>
      <Form
        {...formItemLayout}
        validateMessages={defaultValidateMessages}
        form={form}
        name="bankInfoUpdate"
        initialValues={{
          remember: true,
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Row>
          <Col span={24}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="bankId" hidden>
              <Input />
            </Form.Item>

            {bankRequirements && (
              <>
                {bankRequirements.versions?.length > 1 && (
                  <Form.Item
                    label="Versiyon"
                    name="version"
                    initialValue={activeVersion}
                  >
                    <Select
                      placeholder="Seçiniz"
                      value={activeVersion}
                      onChange={onActiveVersionChange}
                    >
                      {bankRequirements.versions.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {bankRequirements.isUserNameRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.userNameLabel ?? "Kullanıcı Adı"}
                      tooltip={bankRequirements.userNameInfo}
                    />
                  }
                  name="userName"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.userNameLabel?.trim() ?? "kullanıcı adı").toLowerCase()} alanını boş bırakmayın.`,
                  }]}
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isUserCodeRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.userCodeLabel ?? "Kullanıcı Kodu"}
                      tooltip={bankRequirements.userCodeInfo}
                    />
                  }
                  name="userCode"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.userCodeLabel?.trim() ?? "kullanıcı kodu").toLowerCase()} alanını boş bırakmayın.`,
                  }]}
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isPasswordRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.passwordLabel ?? "Şifre"}
                      tooltip={bankRequirements.passwordInfo}
                    />
                  }
                  name="password"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.passwordLabel?.trim() ?? "şifre").toLowerCase()} alanını boş bırakmayın.`,
                  }]}    
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isCustomerNumberRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.customerNumberLabel ?? "Müşteri Numarası"}
                      tooltip={bankRequirements.customerNumberInfo}
                    />
                  }
                  name="customerNumber"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.customerNumberLabel?.trim() ?? "müşteri numarası").toLowerCase()} alanını boş bırakmayın.`,
                  }]}     
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isCorporateCodeRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.corporateCodeLabel ?? "Kurum Kodu"}
                      tooltip={bankRequirements.corporateCodeInfo}
                    />
                  }
                  name="corporateCode"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.corporateCodeLabel?.trim() ?? "kurum kodu").toLowerCase()} alanını boş bırakmayın.`,
                  }]}      
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isServiceUrlRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.serviceUrlLabel ?? "Url"}
                      tooltip={bankRequirements.serviceUrlInfo}
                    />
                  }
                  name="serviceUrl"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.serviceUrlLabel?.trim() ?? "url").toLowerCase()} alanını boş bırakmayın.`,
                  }]}    
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isFirmNameRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.firmNameLabel ?? "Firma Adı"}
                      tooltip={bankRequirements.firmNameInfo}
                    />
                  }
                  name="firmName"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.firmNameLabel?.trim() ?? "firma adı").toLowerCase()} alanını boş bırakmayın.`,
                  }]}
                  >
                    <Input />
                  </Form.Item>
                )}

                {bankRequirements.isFirmKeyRequired && (
                  <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label={bankRequirements.firmKeyLabel ?? "Firma Anahtarı"}
                      tooltip={bankRequirements.firmKeyInfo}
                    />
                  }
                  name="firmKey"
                  rules={[{ 
                    required: true, 
                    whitespace: true,
                    message: `Lütfen ${(bankRequirements.firmKeyLabel?.trim() ?? "firma anahtarı").toLowerCase()} alanını boş bırakmayın.`,
                  }]}
                  >
                    <Input />
                  </Form.Item>
                )}

                {callbackUrl && (
                  <Form.Item
                    label="Tanımlanacak Callback Url"
                    name="callbackUrl"
                    initialValue={callbackUrl}
                  >
                    <Input.Group compact>
                      <Input
                        readOnly
                        value={callbackUrl}
                        style={{ width: "calc(100% - 36px)" }}
                      />
                      <Tooltip title="Kopyala">
                        <Button
                          icon={<CopyOutlined />}
                          onClick={() =>
                            navigator.clipboard.writeText(callbackUrl)
                          }
                        />
                      </Tooltip>
                    </Input.Group>
                  </Form.Item>
                )}

                {useKeyBanks.includes(activeBankId) && (
                  <Form.Item label="Sertfika Dosyası">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={downloadKeyFile}
                    >
                      Dosyayı indir
                    </Button>
                  </Form.Item>
                )}
              </>
            )}

            <Col style={{ textAlign: "right" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                htmlType="submit"
                onClick={() => setLoading(true)}
                loading={loading}
              >
                Güncelle
              </Button>
            </Col>
          </Col>
        </Row>
      </Form>
    </>
  );
};
export default TransactionBankInfoUpdateForm;
